import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";

const EMOJI_CATEGORIES: { label: string; emojis: string[] }[] = [
  {
    label: "Beauty & Spa",
    emojis: ["💅", "💆", "💇", "✨", "🌸", "🌺", "🌹", "🌷", "💐", "🧖", "🧴", "🪮", "💄", "👁️", "👀", "🫦", "💋", "🪷", "🌿", "🍃"],
  },
  {
    label: "Hands & Feet",
    emojis: ["🤲", "👐", "🙌", "👏", "🤝", "✋", "🖐️", "👋", "🦶", "🦵", "💎", "💍", "🎀", "🩹"],
  },
  {
    label: "Faces & People",
    emojis: ["😊", "😍", "🥰", "💃", "🧏", "👸", "🧚", "🧜", "👰", "💁", "🙋", "🤷", "🧑‍🎨"],
  },
  {
    label: "Hearts & Symbols",
    emojis: ["❤️", "🧡", "💛", "💚", "💙", "💜", "🤎", "🖤", "🤍", "💖", "💝", "💕", "💗", "💓", "🫶", "⭐", "🌟", "🔥", "⚡", "☀️", "🌙", "🦋", "🐚"],
  },
  {
    label: "Objects",
    emojis: ["🎨", "🪞", "🛁", "🧼", "🧽", "🧹", "✂️", "📌", "🎁", "🏆", "🥇", "🎯", "🪄", "🔮", "💰", "💳", "🏷️", "📱", "⏰", "🗓️"],
  },
  {
    label: "Food & Drink",
    emojis: ["🍵", "☕", "🧃", "🥂", "🍾", "🫧", "🍬", "🍭", "🍫", "🧁", "🎂", "🍰"],
  },
  {
    label: "Flags & Misc",
    emojis: ["🏳️‍🌈", "🎪", "🎭", "🎬", "🎶", "🎵", "🎤", "🎧", "📸", "🖼️", "🧿", "♾️", "✅", "❌", "⭕", "💯"],
  },
];

interface EmojiPickerProps {
  value: string;
  onChange: (emoji: string) => void;
}

const EmojiPicker = ({ value, onChange }: EmojiPickerProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const allEmojis = EMOJI_CATEGORIES.flatMap((c) => c.emojis);
  const filtered = search
    ? allEmojis.filter(() => true) // emojis don't have text names here, so search filters categories
    : null;

  const filteredCategories = search
    ? EMOJI_CATEGORIES.filter((c) => c.label.toLowerCase().includes(search.toLowerCase()))
    : EMOJI_CATEGORIES;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="h-10 w-full justify-start gap-2 text-left font-normal"
        >
          <span className="text-xl">{value}</span>
          <span className="text-sm text-muted-foreground">Click to change emoji</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <div className="p-2 border-b border-border">
          <Input
            placeholder="Search category..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 text-sm"
          />
        </div>
        <ScrollArea className="h-64 p-2">
          {filteredCategories.map((category) => (
            <div key={category.label} className="mb-3">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium mb-1.5 px-1">
                {category.label}
              </p>
              <div className="grid grid-cols-8 gap-0.5">
                {category.emojis.map((emoji, i) => (
                  <button
                    key={`${emoji}-${i}`}
                    type="button"
                    className={`h-8 w-8 flex items-center justify-center rounded text-lg hover:bg-accent transition-colors ${
                      value === emoji ? "bg-primary/20 ring-1 ring-primary" : ""
                    }`}
                    onClick={() => {
                      onChange(emoji);
                      setOpen(false);
                      setSearch("");
                    }}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          ))}
          {filteredCategories.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">No matching category</p>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};

export default EmojiPicker;
